import { useEffect, useMemo, useRef, useState } from "react";
import { BadgeCheck, ChevronRight, ShieldCheck } from "lucide-react";
import type { InSnipCard } from "../types";
import { buildReceipt, receiptSummary, type ReceiptStrength } from "./receipts";
import { shareProofImage } from "./shareV3";

/**
 * The Receipt line: the card's ONE trust object. Signal score, source count, independent
 * domains and the strength verdict, consolidated into a single elegant line that opens the
 * full Verify drawer. It replaces the photo's Signal chip and the action-row Verify buttons
 * (they were three doors to the same room). Proof sharing lives inside the drawer via
 * ProofShareButton. Composed strictly from real card data; renders nothing without a source.
 * House style: no em dashes anywhere in copy.
 */

const STRENGTH_COPY: Record<ReceiptStrength, string> = {
  corroborated: "Corroborated",
  sourced: "Sourced",
  single: "Single source",
};

export function ReceiptStrip({
  card,
  signal,
  onVerify,
}: {
  card: InSnipCard;
  /** The card's 0-10 signal score, shown as part of the receipt. */
  signal?: number;
  onVerify: () => void;
}) {
  const receipt = useMemo(() => buildReceipt(card), [card]);
  if (!receipt) return null;

  return (
    <button
      type="button"
      className={`receipt-line strength-${receipt.strength}`}
      onClick={onVerify}
      title="The receipt: sources, credibility and the whole picture behind this story"
      aria-label={`Verify this story. ${receiptSummary(receipt)}, ${STRENGTH_COPY[receipt.strength]}.`}
    >
      <ShieldCheck size={14} aria-hidden="true" />
      {typeof signal === "number" && signal > 0 ? (
        <span className="receipt-signal">
          Signal <b>{signal.toFixed(1)}</b>
        </span>
      ) : null}
      <span className="receipt-sources">{receiptSummary(receipt)}</span>
      <em className="receipt-strength">{STRENGTH_COPY[receipt.strength]}</em>
      <ChevronRight size={14} aria-hidden="true" className="receipt-go" />
    </button>
  );
}

/** "Share the proof" for the Verify drawer: renders the branded proof card. */
export function ProofShareButton({ card }: { card: InSnipCard }) {
  const receipt = useMemo(() => buildReceipt(card), [card]);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef<number | null>(null);
  useEffect(() => () => { if (timerRef.current) window.clearTimeout(timerRef.current); }, []);

  if (!receipt) return null;

  async function share() {
    if (!receipt || busy) return;
    setBusy(true);
    const r = await shareProofImage(card, receipt);
    setBusy(false);
    if (r === "failed") return;
    setMsg(r === "shared" ? "Shared" : "Saved");
    timerRef.current = window.setTimeout(() => setMsg(null), 2200);
  }

  return (
    <button
      type="button"
      className="proof-share-btn"
      onClick={() => void share()}
      disabled={busy}
      title="Share this story's receipt as a branded image"
    >
      <BadgeCheck size={14} aria-hidden="true" />
      {msg ?? (busy ? "Creating..." : "Share the proof")}
    </button>
  );
}
